'use client';

import { ReactNode, useEffect } from 'react';
import Modal from 'react-modal';

import { Heading } from '@/components/dom/text-elements';
import Button from '@/components/global/button';
import Icon from '@/components/global/icon';
import { usePosthogTracking } from '@/helpers/hooks/usePosthogTracking';
import cn from '@/lib/cn';
import { type BaseTrackingProperties, type ModalTrackingProperties } from '@/types/tracking';

type TrackingProperties = BaseTrackingProperties & ModalTrackingProperties;
type ModalProps = {
    header?: ReactNode;
    isOpen: boolean;
    onRequestClose: () => void;
    children: ReactNode;
    allowClose?: boolean;
    allowCloseOnShortcut?: boolean;
    size?: '1/3' | '1/2' | '3/4' | '2/3' | 'full';
    closeText?: string;
    allowScroll?: boolean;
    className?: string;
    headerClassName?: string;
    trackingProperties?: TrackingProperties;
    overlayClassName?: string;
    portalClassName?: string;
};

const BaseModal = ({
    header = '',
    isOpen,
    onRequestClose,
    allowClose = true,
    allowCloseOnShortcut = true, // escape or overlay click
    children,
    size = '1/2',
    closeText = '',
    allowScroll = true,
    className,
    headerClassName,
    trackingProperties = {} as TrackingProperties,
    overlayClassName,
    portalClassName,
}: ModalProps) => {
    const { components, eventTypes, trackManualEvent } = usePosthogTracking();

    useEffect(() => {
        const rootElement = document.getElementById('root');
        rootElement && Modal.setAppElement(rootElement);
    }, []);

    useEffect(() => {
        isOpen &&
            trackManualEvent({
                eventType: eventTypes.SHOW_MODAL,
                trackingProperties,
            });
    }, [eventTypes.SHOW_MODAL, isOpen, trackManualEvent, trackingProperties]);

    const onClose = () => {
        onRequestClose();
        trackManualEvent({
            eventType: eventTypes.HIDE_MODAL,
            trackingProperties,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={allowClose && allowCloseOnShortcut}
            shouldCloseOnEsc={allowClose && allowCloseOnShortcut}
            portalClassName={cn('absolute z-3000 bg-white w-full', portalClassName)}
            className={cn(
                'absolute w-1/2 bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[400px] rounded-md shadow-md',
                {
                    'w-1/2': size === '1/2',
                    'w-1/3': size === '1/3',
                    'w-2/3': size === '2/3',
                    'w-3/4': size === '3/4',
                    'w-full': size === 'full',
                },
                className
            )}
            overlayClassName={cn('fixed bg-black bg-opacity-80 top-0 left-0 right-0 bottom-0', overlayClassName)}
        >
            <div className="relative">
                <Heading
                    importance={4}
                    className={cn('p-8 pb-0 mb-1 font-brand-md text-lg text-center', headerClassName)}
                >
                    {header}
                </Heading>
                {allowClose && (
                    <Button
                        type="action"
                        onClick={() => onRequestClose()}
                        color="transparent"
                        className="flex items-center absolute top-1/2 right-0 -translate-y-1/2 z-10"
                        tracking={{
                            eventType: eventTypes.HIDE_MODAL,
                            trackingProperties: {
                                ...trackingProperties,
                                component: components.MODAL_CLOSE_BUTTON,
                            },
                        }}
                    >
                        {closeText}
                        <Icon
                            type="close"
                            size="sm"
                            className="ml-2 mt-1"
                            color="blueYves"
                        />
                    </Button>
                )}
            </div>
            <div className={cn({ 'max-h-[70vh] overflow-y-auto px-2': allowScroll === true })}>{children}</div>
        </Modal>
    );
};

export default BaseModal;
